import ContentConfig from '../config/ContentConfig';
import EventEmitter from '../common/EventEmitter';
import { ApiEvents } from '@kollective-frontend/ksdk-api';
import { DeprecatedEvents } from '../public/events';
import PlaybackTimeout from './PlaybackTimeout';
import autobind from '../autobind';
import ecdn10AgentAPI from './AgentAPI';
import logger from '../log';
import processAgentCommands from './processAgentCommands';
import shouldBlackoutForPrefs from './shouldBlackoutForPrefs';
import timeSinceStart from '../util/timeSinceStart';
import { DeliveryMode, PlayType } from '../common/constants';
import {
  getMimeForSourceType,
  getSourceTypeForMime
} from '../common/utils/sourceTypeUtils';
import { isPositive } from '../common/utils/numbers';

const log = logger.getLogger(__filename);

/**
 * The 10.8 agent returns data in its detection process. In that data are 2
 * variables `live_timeout` and `vod_timeout`. They are synonymous with
 * timeoutAgentStreamingLive and timeoutAgentStreamingVod which are part of
 * the config object. This function checks to see if the agent values are set
 * and if so updates the config to the PlaybackTimeout.
 * @param {object} detection
 */
export function playbackTimeoutConfig(config, detection) {
  if (detection && detection.data) {
    const { live_timeout: live, vod_timeout: vod } = detection.data;
    const tle = config.legacy.timeoutAgentStreamingLive;
    const tvd = config.legacy.timeoutAgentStreamingVod;
    config.legacy.timeoutAgentStreamingLive = positiveOrDefault(live, tle);
    config.legacy.timeoutAgentStreamingVod = positiveOrDefault(vod, tvd);
  }
}

export const doReject = (existingReject, msg) => {
  if (existingReject) {
    return existingReject(msg);
  } else {
    return Promise.reject(msg);
  }
};

export function positiveOrDefault(value, defaultValue) {
  return isPositive(value) ? value : defaultValue;
}

/**
 * Will throw for blackout.
 */
function blackoutForApp(app) {
  switch (app) {
    case 'microsoft.stream':
      return shouldBlackoutForPrefs(['msv:1']).then(res =>
        res
          ? Promise.reject(new Error('Blackout defined in agent prefsList'))
          : Promise.resolve()
      );
    default:
      return Promise.resolve();
  }
}

function canDoPlayback({ urn, blackout, ktoken }) {
  return urn && ktoken && !blackout;
}

export default class ECDN10Playback {
  constructor(options) {
    autobind(this);
    const config = ContentConfig.getInstance();
    this.config = config;
    this.mode = DeliveryMode.ECDN10;
    const {
      content,
      detection,
      detectionSource,
      state,
      switchToCDN,
      realmId,
      realmTicket
    } = options;
    const { sdkConfig } = state.play;
    const { data } = detection || {};
    this.blackout = data.blackout;
    const { legacy, urn } = content;
    this.urn = legacy && legacy.urn ? legacy.urn : urn;
    this.sourceType = state.play.sourceType;
    this.playType = detection.playType;
    this.switchToCDN = switchToCDN;
    this.ktoken = data.ktoken;
    this.authParams = {
      realmId: realmId,
      realmTicket: realmTicket,
      appContext: sdkConfig.integration.name
    };
    this.ts = 0;
    this.progress = 0;
    this.longpollStopped = false;
    this.usePreRoll = config.legacy && config.legacy.usePreRoll;
    if (canDoPlayback(this)) {
      playbackTimeoutConfig(config, detection);
      const playbackTimeout = new PlaybackTimeout();
      playbackTimeout.init(
        timeSinceStart(state),
        detection.playType,
        detectionSource.url,
        this.sourceType
      );
      this.withTimeout = promise =>
        Promise.race([playbackTimeout.getPromise(), promise]);
    }
  }

  async init() {
    if (!canDoPlayback(this)) {
      return Promise.reject(
        new Error(
          `ECDN10 agent cannot perform playback - urn: ${!!this
            .urn}, ktoken: ${!!this.ktoken}, blackout: ${this.blackout}`
        )
      );
    }

    if (this.playType === PlayType.VOD) {
      await blackoutForApp(this.config.app);
    }

    return this.withTimeout(
      new Promise((resolve, reject) => {
        ecdn10AgentAPI.startSession({
          usePreRoll: this.usePreRoll,
          body: Object.assign({ ktoken: this.ktoken }, this.authParams),
          onSuccess: (data, request) => resolve({ data, request }),
          onFailure: reject,
          onTimeout: reject
        });
      })
    );
  }

  /**
   * @param sourceInfo - the object containing the urls to setup the video
   * @param module - the Ksdk/MsftPlugin, which we can call and reset the sources.
   */
  prepDelivery(sourceInfo, module) {
    if (!module) {
      log.debug('No module for prepDelivery');
      return;
    }
    const config = this.config;
    if (config && config.legacy && config.legacy.dontResetSource) {
      log.info('Configured not to replace source');
      return;
    }
    if (module.playerInteraction && module.playerInteraction.clearSource) {
      module.playerInteraction.clearSource();
    }
  }

  /**
   * This method creates a promise with a timeout if the parameters existingResolve
   * and existingReject are not set, if they are set then this calls the agent.prefetch
   * using those parameters as the resolve and reject methods.
   *
   * @param sourceInfo - the object containing the urls to setup the video
   * @param module - the plugin, which is used by prepDelivery
   * @param existingResolve - a function (either null or the resolve function from the
   * original withTimeout call)
   * @param existingReject - a function (either null or the reject function from the
   * original withTimeout call)
   */
  startDelivery(sourceInfo, module, existingResolve, existingReject) {
    if (!sourceInfo) {
      return doReject(existingReject, 'No source info passed to startDelivery');
    }
    this.prepDelivery(sourceInfo, module);
    const len = (sourceInfo.sources && sourceInfo.sources.length) || 0;
    if (len === 0) {
      if (sourceInfo.legacySourceTypeFallback) {
        log.debug('startDelivery: Falling back to legacy source type');
        this.sourceType = sourceInfo.legacySourceTypeFallback;
        const url = ecdn10AgentAPI.createStreamURL(this.urn, this.sourceType);
        const mime = getMimeForSourceType(this.sourceType);
        sourceInfo.sources = [{ url, mime }];
      } else {
        return doReject(existingReject, 'No sources passed to startDelivery');
      }
    }
    this.longpollStopped = false;

    if (existingResolve && existingReject) {
      log.debug('startDelivery updating prefetch');
      this.callPrefetch(sourceInfo, existingResolve, existingReject);
    } else {
      return this.withTimeout(
        new Promise((resolve, reject) =>
          this.callPrefetch(sourceInfo, resolve, reject)
        )
      );
    }
  }

  callPrefetch(sourceInfo, resolve, reject) {
    ecdn10AgentAPI.prefetch({
      urn: this.urn,
      streamType: this.sourceType,
      params: { ktoken: this.ktoken },
      onSuccess: () => resolve(this.longpollPromise(sourceInfo)),
      onFailure: request =>
        this.handlePrefetchFailure(request, sourceInfo, resolve, reject),
      onTimeout: () => resolve(this.longpollPromise(sourceInfo))
    });
  }

  handlePrefetchFailure(request, sourceInfo, resolve, reject) {
    if (request && request.status >= 400 && request.status < 500) {
      resolve(this.longpollPromise(sourceInfo));
    } else {
      reject();
    }
  }

  longpollPromise(sourceInfo) {
    return new Promise((resolve, reject) => {
      this.longpoll(sourceInfo, resolve, reject);
    });
  }

  ready(sourceInfo, resolve) {
    log.info('ecdn10: prefetch completed, ready to play');
    resolve(this.prepareSources(sourceInfo));
    this.listenToAgentCommands();
  }

  longpoll(sourceInfo, resolve, reject) {
    ecdn10AgentAPI.longpoll({
      params: { ktoken: this.ktoken, ts: this.ts },
      onSuccess: data => {
        const { ts, cmd, params } = data;
        this.ts = ts;
        processAgentCommands({
          cmd,
          params,
          onReady: () => {
            this.ready(sourceInfo, resolve);
          },
          onProgress: p => {
            log.info('ecdn10:a.progress=' + p);
            const emitter = EventEmitter.getInstance();
            const data = { progress: p };
            // in case if progress regresses back to 0 - we should start priming over
            emitter.emit(ApiEvents.ECDN10_PROGRESS, { data });
            emitter.emit(DeprecatedEvents.LEGACY_PROGRESS, data);
            if (this.progress > 0 && p == 0) {
              this.stop().then(() =>
                // Pass the resolve/reject functions back to startDelivery to
                // keep the chain intact with the plugin
                this.startDelivery(sourceInfo, null, resolve, reject)
              );
            } else {
              this.progress = p;
              this.longpoll(sourceInfo, resolve, reject);
            }
          },
          switchToCDN: this.processSwitchToCDN,
          retry: () => {
            this.longpoll(sourceInfo, resolve, reject);
          }
        });
      },
      onFailure: r => {
        log.fatal('ecdn10:a.lp.f=' + r.status);
        reject();
      },
      onTimeout: () => {
        log.info('ecdn10:a.lp.to');
        this.longpoll(sourceInfo, resolve, reject);
      }
    });
  }

  listenToAgentCommands() {
    if (this.longpollStopped) {
      return;
    }
    ecdn10AgentAPI.longpoll({
      params: { ktoken: this.ktoken, ts: this.ts },
      onSuccess: data => {
        const { ts, cmd, params } = data;
        this.ts = ts;
        processAgentCommands({
          cmd,
          onProgress: () => {},
          onReady: () => {},
          params,
          switchToCDN: this.processSwitchToCDN,
          retry: () => {}
        });
        this.listenToAgentCommands();
      },
      onFailure: r => {
        log.fatal('ecdn10:a.ac.f=' + r.status);
      },
      onTimeout: () => {
        log.info('ecdn10:a.ac.to');
        this.listenToAgentCommands();
      }
    });
  }

  processSwitchToCDN() {
    this.stop();
    if (this.switchToCDN) {
      this.switchToCDN();
    }
  }

  /**
   * If there is a list of sources, use the mime for each object to create a stream url
   * @param {object} sourceInfo
   * @returns {}
   */
  prepareSources(sourceInfo) {
    if (sourceInfo && sourceInfo.sources) {
      const sources = sourceInfo.sources.map(source => {
        // sourceInfo will likely have sources with {src: ..., type: ...}.mime check is here for posterity.
        const sourceType = getSourceTypeForMime(
          source.type || source.mime,
          source.src
        );
        const kurl = ecdn10AgentAPI.createStreamURL(this.urn, sourceType);
        return { ...source, kurl };
      });
      return { ...sourceInfo, sources };
    } else {
      return sourceInfo;
    }
  }

  stop() {
    this.progress = 0;
    this.longpollStopped = true;
    return new Promise(resolve => {
      ecdn10AgentAPI.stopStream({
        params: Object.assign({ urn: this.urn }, this.authParams),
        onSuccess: resolve,
        onFailure: resolve,
        onTimeout: resolve
      });
    });
  }
}
